import { useEffect, useState } from 'react';
import {
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  CategoryChips,
  DeliverySummaryBanner,
  MenuItemCard,
  PriceText,
  StickyActionFooter,
} from '../components';
import { useCateringService } from '../app/CateringProvider';
import { getCartQuantity, toOrderDraft } from '../state/orderDraft';
import { useOrderDraft } from '../state/OrderDraftProvider';
import type { Category, MenuItem, OrderQuote } from '../types/catering';
import { ORDER_PATHS, productPath, useRouter } from '../router';
import { ApiError } from '../api/httpClient';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minHeight: '100%',
    paddingBottom: '5.5rem',
  },
  summary: {
    marginBottom: tokens.spacingVerticalXS,
  },
  heading: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    marginInline: `-${tokens.spacingHorizontalS}`,
  },
  empty: {
    padding: tokens.spacingVerticalXL,
    textAlign: 'center',
  },
  footer: {
    marginTop: 'auto',
  },
  estimate: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: `${tokens.spacingVerticalS}`,
    padding: `0`,
  },
});

const OUTLET_NOT_OPEN_ERROR_CODE = '001';

function getMenuErrorMessage(error: unknown): string {
  return error instanceof ApiError && error.errorCode === OUTLET_NOT_OPEN_ERROR_CODE
    ? 'The outlet is not open at the selected time.'
    : 'We could not load the catering menu.';
}

export default function MenuPage() {
  const styles = useStyles();
  const service = useCateringService();
  const { state, dispatch } = useOrderDraft();
  const { navigate } = useRouter();
  const [categories, setCategories] = useState<Category[]>([]);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [quote, setQuote] = useState<OrderQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const selectedDeliveryTimestamp = state.setup?.window.tsDeliveryDesired;

  useEffect(() => {
    let isCurrent = true;
    setError(null);

    service
      .getCategories()
      .then((loadedCategories) => {
        if (!isCurrent) return;
        setCategories(loadedCategories);
        setSelectedCategoryId((current) => current ?? loadedCategories[0]?.id ?? null);
      })
      .catch((error: unknown) => {
        if (!isCurrent) return;
        setError(getMenuErrorMessage(error));
      })
      .finally(() => {
        if (isCurrent && selectedCategoryId === null) setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [service]);

  useEffect(() => {
    if (selectedCategoryId === null) return;

    let isCurrent = true;
    setIsLoading(true);
    setError(null);

    service
      .getMenu({ categoryId: selectedCategoryId, tsDeliveryDesired: selectedDeliveryTimestamp })
      .then((loadedItems) => {
        if (isCurrent) setItems(loadedItems);
      })
      .catch((error: unknown) => {
        if (isCurrent) setError(getMenuErrorMessage(error));
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [service, selectedCategoryId, selectedDeliveryTimestamp]);

  useEffect(() => {
    const draft = toOrderDraft(state);
    if (!draft) {
      setQuote(null);
      setQuoteError(null);
      return;
    }

    let isCurrent = true;
    service
      .quoteOrder(draft)
      .then((nextQuote) => {
        if (!isCurrent) return;
        setQuote(nextQuote);
        setQuoteError(null);
      })
      .catch(() => {
        if (!isCurrent) return;
        setQuote(null);
        setQuoteError('We could not update the order subtotal.');
      });

    return () => {
      isCurrent = false;
    };
  }, [service, state]);

  const updateQuantity = (itemId: number, nextQuantity: number) => {
    const currentQuantity = getCartQuantity(state, itemId);
    if (nextQuantity <= 0) {
      dispatch({ type: 'removeCartItem', itemId });
      return;
    }

    if (currentQuantity === 0) {
      dispatch({ type: 'addCartItem', itemId });
    }
    for (let quantity = Math.max(1, currentQuantity); quantity < nextQuantity; quantity += 1) {
      dispatch({ type: 'incrementCartItem', itemId });
    }
    for (let quantity = currentQuantity; quantity > nextQuantity; quantity -= 1) {
      dispatch({ type: 'decrementCartItem', itemId });
    }
  };

  const totalDishes = state.lines.reduce((total, line) => total + line.portionsPerPerson, 0);

  return (
    <main className={styles.root}>
      {state.setup ? (
        <div className={styles.summary}>
          <DeliverySummaryBanner setup={state.setup} onEdit={() => navigate(ORDER_PATHS.setup)} />
        </div>
      ) : (
        <MessageBar intent="warning">
          <MessageBarBody>Complete setup details before choosing menu items.</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.heading}>
        <Text as="h1" size={400} weight="semibold">
          Menu ({state.setup?.window.label ?? '12:00'})
        </Text>
        <Text className={styles.hint} size={200}>
          Serving {state.setup?.headcount ?? 0} · prices per person
        </Text>
      </div>

      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      <CategoryChips
        categories={categories}
        selectedId={selectedCategoryId}
        onSelect={setSelectedCategoryId}
      />

      {isLoading ? (
        <Spinner label="Loading catering menu" />
      ) : items.length === 0 ? (
        <Text className={styles.empty}>No menu items are available.</Text>
      ) : (
        <div className={styles.list}>
          {items.map((item) => (
            <MenuItemCard
              key={item.id}
              item={item}
              quantity={getCartQuantity(state, item.id)}
              onQuantityChange={(quantity) => updateQuantity(item.id, quantity)}
              onDetails={() => navigate(productPath(item.id))}
            />
          ))}
        </div>
      )}

      <div className={styles.footer}>
        <StickyActionFooter
          label="Next order"
          onClick={() => navigate(ORDER_PATHS.review)}
          disabled={state.lines.length === 0 || isLoading || !quote}
        >
          <div className={styles.estimate}>
            <Text className={styles.hint}>
              {totalDishes} {totalDishes === 1 ? 'dish' : 'dishes'} · subtotal
            </Text>
            {quote ? (
              <PriceText
                price={quote.subtotal}
                size="small"
                weight="semibold"
                includeCurrency={false}
              />
            ) : (
              <Text className={styles.hint}>-</Text>
            )}
          </div>
          {quoteError ? (
            <MessageBar intent="error">
              <MessageBarBody>{quoteError}</MessageBarBody>
            </MessageBar>
          ) : null}
        </StickyActionFooter>
      </div>
    </main>
  );
}
